/**
 * The two inputs that let a (feature page, locale) pair "graduate" from the `noindex, follow`
 * placeholder phase 3 step 1 gave every locale of the 14 marketing feature pages, to a real,
 * indexable page with its own title/meta and hreflang — phase 3 step 2 of
 * work/locale-coverage-unification.md in stippl-knowledge. Consumed by
 * `generateLocalisedFeatureRoutes` / `applyFeatureHreflangToEnglishRoutes` in
 * `marketing/marketingRoutes.tsx`; see that file's doc comments for the exact gating rules.
 */

import { LOCALISED_FEATURE_PATHS, LocalisedFeaturePath, NonEnglishLocale } from "i18n/urlLocale";

export interface FeaturePageLocaleMeta {
	title: string;
	description: string;
}

export type FeaturePageLocaleMetaMap = Partial<Record<LocalisedFeaturePath, Partial<Record<NonEnglishLocale, FeaturePageLocaleMeta>>>>;

/**
 * Hand-written per market with the `seo` skill (keyword-researched, never machine-translated) —
 * the same precedent as `HOME_META` in `marketingRoutes.tsx`. Source deck:
 * stippl-knowledge website/feature-pages/localised-meta.md.
 *
 * All 84 pairs were written 2026-09-24 against Google Keyword Planner volumes per market (the
 * deck holds the keyword table and the reasoning per page). Titles <= 60 characters, descriptions
 * <= 155, no em dashes. A (page, locale) pair missing from this map keeps the step-1 behaviour —
 * see `generateLocalisedFeatureRoutes`.
 *
 * A locale entering this map is only half of graduation — see `FEATURE_PAGES_WITH_LOCALISED_BODY`
 * below for the other gate.
 */
export const FEATURE_PAGE_LOCALE_META: FeaturePageLocaleMetaMap = {
	"/itinerary-planner": {
		nl: {
			title: "Reisschema maken, dag voor dag | Stippl",
			description: "Maak je reisschema dag voor dag: je route op de kaart, verblijven en activiteiten op één plek. Voor altijd gratis, voor elke reis.",
		},
		de: {
			title: "Reiseroute planen, Tag für Tag | Stippl",
			description: "Plane deine Reiseroute Tag für Tag: Route auf der Karte, Unterkünfte und Aktivitäten an einem Ort. Für immer kostenlos, für jede Reise.",
		},
		fr: {
			title: "Planificateur d'itinéraire de voyage gratuit | Stippl",
			description:
				"Créez votre itinéraire de voyage jour par jour, gratuitement avec Stippl : carte du trajet, hébergements et activités réunis au même endroit.",
		},
		es: {
			title: "Itinerario de viaje día a día | Stippl",
			description:
				"Crea tu itinerario de viaje día a día: ruta en el mapa, alojamientos y actividades en un solo sitio. Gratis para siempre, para cualquier viaje.",
		},
		it: {
			title: "Organizza il tuo viaggio giorno per giorno | Stippl",
			description:
				"L'app per organizzare viaggi di Stippl: itinerario giorno per giorno, percorso sulla mappa, alloggi e attività in un unico posto. Gratis per sempre.",
		},
		pt: {
			title: "Roteiro de viagem dia a dia, grátis | Stippl",
			description:
				"Crie seu roteiro de viagem dia a dia com o Stippl: rota no mapa, hospedagens e atividades num só lugar. Grátis para sempre, em qualquer viagem.",
		},
	},
	"/ai-travel-planner": {
		nl: {
			title: "Reis plannen met AI: een echt reisschema | Stippl",
			description:
				"Plan je reis met AI en krijg een aanpasbaar reisschema dag voor dag, gekoppeld aan je budget en paklijst. Niet zomaar een antwoord, een hele reis.",
		},
		de: {
			title: "Reiseplanung mit KI: mehr als ein Chat | Stippl",
			description: "Reiseplanung mit KI, ohne im Chat zu versinken: Stippl macht daraus eine bearbeitbare Route Tag für Tag, mit Budget, Packliste und Gruppe.",
		},
		fr: {
			title: "Planifiez votre voyage avec l'IA | Stippl",
			description:
				"Planifiez votre voyage avec l'IA et obtenez un vrai itinéraire jour par jour, modifiable et partageable, relié à votre budget et à votre groupe.",
		},
		es: {
			title: "IA para viajes: tu itinerario, no un texto | Stippl",
			description: "La IA de Stippl no te da un simple texto: crea tu itinerario día a día, editable y conectado a tu presupuesto, tu equipaje y tu grupo.",
		},
		it: {
			title: "IA per viaggi: un itinerario vero, non una chat | Stippl",
			description:
				"L'IA di Stippl crea un itinerario giorno per giorno modificabile, condiviso col gruppo e collegato a budget e bagagli. Non una chat: un viaggio vero.",
		},
		pt: {
			title: "IA para viagem: roteiro pronto, não só texto | Stippl",
			description:
				"A IA do Stippl gera um roteiro dia a dia editável e compartilhável, não só uma resposta em texto. Conectado ao orçamento, à mala e ao seu grupo.",
		},
	},
	"/budget-planner": {
		nl: {
			title: "Vakantiebudget plannen, tot op de euro | Stippl",
			description: "Plan je vakantiebudget vooraf en houd elke uitgave bij tijdens je reis, per categorie en per dag. Gratis reisbudget-planner van Stippl.",
		},
		de: {
			title: "Reisebudget planen: wisse, wohin dein Geld geht | Stippl",
			description: "Plane dein Urlaubsbudget kostenlos und behalte unterwegs jede Ausgabe im Blick, nach Kategorie, Tag und Person. Direkt in deiner Reise.",
		},
		fr: {
			title: "Budget voyage gratuit : suivez chaque dépense | Stippl",
			description:
				"Planifiez votre budget voyage et suivez chaque dépense en route, par catégorie, par jour et par personne. Gratuit, directement dans votre itinéraire.",
		},
		es: {
			title: "Presupuesto de viaje: controla cada gasto | Stippl",
			description: "Calcula el presupuesto de tu viaje y controla cada gasto por categoría, día y persona. Gratis, integrado en tu itinerario de Stippl.",
		},
		it: {
			title: "Budget di viaggio: sai dove vanno i tuoi soldi | Stippl",
			description: "Pianifica il budget di viaggio e tieni traccia delle spese per categoria, giorno e persona, in oltre 100 valute. Gratis su Stippl.",
		},
		pt: {
			title: "Orçamento de viagem: saiba onde vai seu dinheiro | Stippl",
			description: "Planeje o orçamento da viagem e controle cada gasto por categoria, dia e pessoa, direto no seu roteiro. Grátis para sempre no Stippl.",
		},
	},
	"/travel-tracker": {
		nl: {
			title: "Reis tracker: volg je route live op de kaart | Stippl",
			description: "Breng je route in kaart terwijl je reist, schrijf een fotodagboek bij elke stop en deel alles met één link. Gratis op web, iOS en Android.",
		},
		de: {
			title: "Reise tracken: deine Route live auf der Karte | Stippl",
			description:
				"Verfolge deine Reise auf einer Live-Karte, schreib unterwegs dein Reisetagebuch und teile alles mit einem Link. Kostenlos auf Web, iOS und Android.",
		},
		fr: {
			title: "Suivi de voyage : votre trajet en direct | Stippl",
			description:
				"Suivez votre itinéraire sur une carte en direct, rédigez votre carnet à chaque étape et partagez votre voyage en un lien. Gratuit sur Stippl.",
		},
		es: {
			title: "Rastreador de viajes: tu ruta en vivo | Stippl",
			description: "Sigue tu ruta en un mapa en tiempo real, parada a parada, y compártela con quien quieras. Gratis, con diario y fotos incluidos.",
		},
		it: {
			title: "Traccia il tuo viaggio su una mappa live | Stippl",
			description: "Traccia il tuo viaggio su una mappa in tempo reale, scrivi il diario a ogni tappa e condividilo con chi vuoi. Gratis con Stippl.",
		},
		pt: {
			title: "Rastreador de viagem com mapa e diário | Stippl",
			description: "Acompanhe sua rota em um mapa ao vivo, escreva seu diário com fotos e compartilhe com um link. Usado por mais de 1,5 milhão de viajantes.",
		},
	},
	"/scratch-map": {
		nl: {
			title: "Kraskaart online: al je bezochte landen | Stippl",
			description:
				"Maak je online kraskaart (scratch map) van bezochte landen en je bucketlist. Personaliseer 'm en deel je reisgeschiedenis. Gratis bij Stippl.",
		},
		de: {
			title: "Rubbelkarte online: deine Scratch Map | Stippl",
			description:
				"Erstelle deine Rubbelkarte online: markiere bereiste Länder und deine Bucket List, gestalte sie selbst und teile sie. Kostenlos bei Stippl.",
		},
		fr: {
			title: "Carte à gratter en ligne, gratuite | Stippl",
			description: "Créez votre carte du monde à gratter en ligne : cochez les pays visités, ajoutez vos envies de voyage et partagez-la. Gratuite sur Stippl.",
		},
		es: {
			title: "Mapa de países visitados, rascable y online | Stippl",
			description: "Crea tu mapa rascable online: marca los países visitados y tu lista de deseos, y compártelo con tus amigos. Gratis en Stippl.",
		},
		it: {
			title: "Mappa da grattare online, gratis | Stippl",
			description:
				"Crea la tua mappa da grattare online: segna i paesi visitati, aggiungi la bucket list e condividila con gli amici. Gratis su Stippl, web e app.",
		},
		pt: {
			title: "Mapa de raspar online grátis | Stippl",
			description: "Crie seu mapa mundi de raspar online: marque os países que você visitou, adicione sua lista de desejos e compartilhe. Grátis no Stippl.",
		},
	},
	"/expense-tracker": {
		nl: {
			title: "Kosten delen op reis: gratis app | Stippl",
			description:
				"Kosten delen met je reisgenoten: voeg uitgaven toe onderweg, verreken ze in de app en reken valuta automatisch om. Gratis voor iOS en Android.",
		},
		de: {
			title: "Kosten teilen auf Reisen: kostenlose App | Stippl",
			description:
				"Erfasse Ausgaben unterwegs, teile Kosten mit Mitreisenden und rechne automatisch ab, auch in Fremdwährung. Kostenlose App für iOS und Android.",
		},
		fr: {
			title: "Partagez les dépenses de voyage | Stippl",
			description:
				"Suivez chaque dépense en voyage, partagez les frais avec vos compagnons et convertissez les devises automatiquement. Gratuit sur iOS et Android.",
		},
		es: {
			title: "App para dividir gastos de viaje | Stippl",
			description:
				"Registra cada gasto sobre la marcha, divide los gastos con tus compañeros de viaje y convierte divisas al instante. App gratis en iOS y Android.",
		},
		it: {
			title: "App per dividere le spese di viaggio | Stippl",
			description:
				"Dividi le spese di viaggio con il gruppo: aggiungile in movimento, Stippl calcola chi deve cosa e converte le valute in automatico. Gratis.",
		},
		pt: {
			title: "Controle de gastos de viagem: divida com amigos | Stippl",
			description:
				"Registre, categorize e divida gastos de viagem com seus companheiros em tempo real. Converta moedas automaticamente ao trocar de país. Grátis.",
		},
	},
	"/packing-list": {
		nl: {
			title: "Vakantie paklijst maken, altijd compleet | Stippl",
			description: "Maak gratis je vakantie paklijst: kant-en-klare sjablonen, deel 'm met je groep en vink af terwijl je pakt. Naast je reisschema en budget.",
		},
		de: {
			title: "Packliste für den Urlaub: kostenlos erstellen | Stippl",
			description:
				"Erstelle deine Packliste für den Urlaub direkt in der Reise: fertige Vorlagen, geteilte Listen und Zuweisung pro Person. Kostenlos, Web und App.",
		},
		fr: {
			title: "Liste valise vacances : modèles et partage | Stippl",
			description:
				"Créez votre liste de valise pour les vacances dans votre voyage Stippl : modèles prêts à l'emploi, listes partagées et objets attribués. Gratuit.",
		},
		es: {
			title: "Lista de maleta para tu viaje, gratis | Stippl",
			description:
				"¿Qué llevar en la maleta? Crea tu lista con plantillas para cada tipo de viaje, compártela con tu grupo y marca cada artículo. Gratis en Stippl.",
		},
		it: {
			title: "Lista valigia: cosa mettere in valigia | Stippl",
			description: "Crea la tua lista valigia con modelli pronti, liste condivise col gruppo e oggetti assegnati a ogni persona. Gratis, sul web e nell'app.",
		},
		pt: {
			title: "Checklist de viagem e lista de mala, grátis | Stippl",
			description: "Monte sua checklist de viagem com modelos prontos, compartilhe a lista de mala com o grupo e atribua itens. Direto no seu roteiro. Grátis.",
		},
	},
	"/your-map": {
		nl: {
			title: "Je persoonlijke reiskaart | Stippl",
			description: "Sla je favoriete plekken op, bouw je bucketlist en deel je reiskaart met vrienden. Gratis in Stippl, voor elke bestemming.",
		},
		de: {
			title: "Persönliche Reisekarte: Orte speichern & teilen | Stippl",
			description: "Speichere deine Lieblingsorte auf einer persönlichen Reisekarte, baue deine Bucket List und teile sie mit Freunden. Kostenlos bei Stippl.",
		},
		fr: {
			title: "Votre carte de voyage personnelle | Stippl",
			description: "Épinglez vos lieux favoris pour chaque destination, créez votre liste d'envies et partagez votre carte de voyage avec vos amis. Gratuit.",
		},
		es: {
			title: "Tu mapa de viajes personal | Stippl",
			description: "Guarda tus lugares favoritos de cada destino, crea tu lista de deseos y comparte tu mapa de viajes con amigos. Gratis en Stippl.",
		},
		it: {
			title: "La tua mappa di viaggio personale | Stippl",
			description: "La tua mappa di viaggio personale: salva i posti che ami, crea la bucket list e condividila con gli amici. Gratis su Stippl.",
		},
		pt: {
			title: "Mapa de viagem pessoal: salve seus lugares | Stippl",
			description: "Salve lugares favoritos, monte sua lista de desejos e compartilhe seu mapa de viagem com amigos. Grátis no Stippl, na web e no app.",
		},
	},
	"/social-travel-platform": {
		nl: {
			title: "Reiscommunity: volg reizigers, deel je reizen | Stippl",
			description: "Volg vrienden en andere reizigers, deel je reizen en ontdek plekken via mensen die je vertrouwt. De gratis reiscommunity van Stippl.",
		},
		de: {
			title: "Reise-Community: Reisenden folgen, Reisen teilen | Stippl",
			description: "Folge Reisenden, teile deine Reiserouten und entdecke Orte über echte Empfehlungen. Die kostenlose Reise-Community von Stippl.",
		},
		fr: {
			title: "Communauté de voyageurs : suivez et partagez | Stippl",
			description:
				"Suivez vos amis et d'autres voyageurs, partagez vos aventures et découvrez leurs meilleures adresses. La communauté de voyage gratuite de Stippl.",
		},
		es: {
			title: "Comunidad de viajeros: comparte tus viajes | Stippl",
			description: "Conecta con amigos y viajeros afines, sigue sus aventuras y descubre los mejores lugares. Gratis en Stippl.",
		},
		it: {
			title: "Community di viaggiatori: segui e condividi | Stippl",
			description:
				"Segui amici e viaggiatori che ammiri, condividi le tue avventure e scopri posti consigliati da persone reali. La community di viaggio di Stippl.",
		},
		pt: {
			title: "Comunidade de viajantes: siga e compartilhe | Stippl",
			description: "Siga amigos e viajantes, compartilhe roteiros e descubra lugares recomendados por pessoas de verdade. Grátis na web, iOS e Android.",
		},
	},
	"/travel-journal": {
		nl: {
			title: "Reisdagboek bijhouden, online en in de app | Stippl",
			description: "Schrijf je reisdagboek, voeg foto's toe en deel je avonturen met vrienden. Gratis op web, iOS en Android.",
		},
		de: {
			title: "Reisetagebuch-App: Erlebnisse festhalten | Stippl",
			description:
				"Schreib dein Reisetagebuch online oder in der App, mit Fotos zu jedem Reiseziel, und teile es mit Freunden. Kostenlos auf Web, iOS und Android.",
		},
		fr: {
			title: "Carnet de voyage en ligne, gratuit | Stippl",
			description:
				"Écrivez votre carnet de voyage, ajoutez vos photos et partagez vos souvenirs avec vos amis. Journal de voyage gratuit, sur web, iOS et Android.",
		},
		es: {
			title: "Diario de viaje online gratis | Stippl",
			description: "Escribe tu diario de viaje y sube tus fotos desde cualquier destino. Compártelo con tus amigos. Gratis en Stippl, en web, iOS y Android.",
		},
		it: {
			title: "Diario di viaggio online, gratis | Stippl",
			description:
				"Scrivi le tue avventure, carica le foto e rivivi ogni viaggio: il diario di viaggio digitale di Stippl, collegato al tuo itinerario. Gratis.",
		},
		pt: {
			title: "Diário de viagem online grátis | Stippl",
			description: "Escreva seu diário de viagem com fotos, direto do celular ou do navegador. Compartilhe com amigos e reviva sua viagem. Grátis no Stippl.",
		},
	},
	"/travel-reel": {
		nl: {
			title: "Reisvideo maken van je hele reis | Stippl",
			description: "Stippl maakt automatisch een 3D-reisvideo van je route en foto's. Geen montage nodig, gratis en klaar in een paar minuten.",
		},
		de: {
			title: "Reisevideo erstellen: dein 3D Travel Reel | Stippl",
			description: "Stippl macht aus deiner Route und deinen Fotos automatisch ein 3D-Reisevideo. Kein Schnitt nötig, bereit für Instagram Reels und TikTok.",
		},
		fr: {
			title: "Vidéo de voyage automatique en 3D | Stippl",
			description:
				"Générez automatiquement une vidéo 3D de votre trajet et de vos photos, sans montage. Votre reel de voyage est prêt en quelques minutes. Gratuit.",
		},
		es: {
			title: "Vídeo de viaje en 3D automático | Stippl",
			description: "Genera un vídeo en 3D de toda tu ruta y tus fotos, sin editar nada. Tu reel de viaje estará listo en minutos. Gratis.",
		},
		it: {
			title: "Video di viaggio 3D generato in automatico | Stippl",
			description: "Il tuo percorso e le tue foto in un video di viaggio 3D, pronto in pochi minuti. Nessun montaggio, gratis e senza filigrana.",
		},
		pt: {
			title: "Vídeo de viagem em 3D automático | Stippl",
			description: "Transforme sua viagem em um vídeo 3D com sua rota e fotos, gerado automaticamente. Sem edição, sem software. Pronto em minutos.",
		},
	},
	"/esim": {
		nl: {
			title: "eSIM voor het buitenland, in je reisplanner | Stippl",
			description:
				"Regel je eSIM voor het buitenland direct in je reisplanner. Verbinding in 150+ landen, geen roamingkosten. Verdien een gratis eSIM met Stippl-punten.",
		},
		de: {
			title: "eSIM fürs Ausland, direkt in deiner Reise | Stippl",
			description:
				"Deine Travel eSIM fürs Ausland direkt in der Reiseplanung: sofort verbunden in 150+ Ländern, ohne Roaming. Mit Stippl Punkten gibt es eine gratis.",
		},
		fr: {
			title: "eSIM voyage intégrée à votre itinéraire | Stippl",
			description:
				"Votre eSIM voyage directement dans Stippl : connexion instantanée dans plus de 150 pays, sans frais d'itinérance. Gagnez une eSIM gratuite avec vos points.",
		},
		es: {
			title: "eSIM para viajar, integrada en tu itinerario | Stippl",
			description:
				"Tu eSIM para viajar, dentro de Stippl: conexión instantánea en más de 150 países, sin roaming ni cambiar de SIM. Consigue una gratis con puntos.",
		},
		it: {
			title: "eSIM per l'estero, nel tuo itinerario | Stippl",
			description:
				"La tua eSIM per l'estero dentro l'itinerario: connessione immediata in oltre 150 paesi, zero roaming, zero cambio SIM. Gratis con i punti Stippl.",
		},
		pt: {
			title: "eSIM internacional direto no seu roteiro | Stippl",
			description:
				"Seu eSIM internacional direto no Stippl: conecte-se em mais de 150 países sem roaming nem trocar de chip. Ganhe um grátis com pontos Stippl.",
		},
	},
	"/photobook": {
		nl: {
			title: "Vakantie fotoboek maken van je reisdagboek | Stippl",
			description:
				"Maak van je Stippl-reisdagboek een gedrukt fotoboek met harde kaft: foto's, routekaart en verhalen. Gratis te maken, geen abonnement nodig.",
		},
		de: {
			title: "Fotobuch vom Urlaub, aus deinem Reisetagebuch | Stippl",
			description:
				"Verwandle dein Reisetagebuch in ein gedrucktes Hardcover-Fotobuch: Fotos, Routenkarte und Geschichten, automatisch gestaltet. Kostenlos erstellen.",
		},
		fr: {
			title: "Livre photo de voyage imprimé | Stippl",
			description:
				"Transformez votre carnet de voyage en livre photo à couverture rigide : photos, carte d'itinéraire et récits. Gratuit à créer, sans abonnement.",
		},
		es: {
			title: "Álbum de fotos de viaje impreso | Stippl",
			description:
				"Convierte tu diario de viaje en un fotolibro de tapa dura: fotos, ruta y notas maquetadas automáticamente. Crearlo es gratis, sin suscripción.",
		},
		it: {
			title: "Fotolibro di viaggio stampato dal diario | Stippl",
			description:
				"Foto, percorso e pagine di diario impaginati in automatico in un fotolibro di viaggio con copertina rigida. Crearlo è gratis, senza abbonamento.",
		},
		pt: {
			title: "Fotolivro de viagem impresso do seu diário | Stippl",
			description: "Transforme seu diário de viagem em um fotolivro de capa dura: fotos, mapa da rota e anotações, montado automaticamente. Criar é grátis.",
		},
	},
	"/pro": {
		nl: {
			title: "Stippl Pro: onbeperkt plannen met AI en meer",
			description:
				"Ga Pro voor onbeperkte AI-reisschema's, PDF-export en agenda-synchronisatie. Eén abonnement, alle Stippl-functies ontgrendeld, altijd opzegbaar.",
		},
		de: {
			title: "Stippl Pro: unbegrenzte KI-Reiserouten & mehr",
			description: "Unbegrenzte KI-Reiserouten, PDF-Export und Kalender-Sync. Ein Abo, jede Stippl-Funktion freigeschaltet, jederzeit kündbar.",
		},
		fr: {
			title: "Stippl Pro : itinéraires IA illimités et export PDF",
			description:
				"Débloquez les itinéraires IA illimités, l'export PDF et la synchro calendrier. Un abonnement, toutes les fonctions Stippl, résiliable à tout moment.",
		},
		es: {
			title: "Stippl Pro: itinerarios con IA sin límites",
			description: "Genera itinerarios con IA sin límites, exporta a PDF y sincroniza tu calendario. Un plan, todo incluido, cancela cuando quieras.",
		},
		it: {
			title: "Stippl Pro: IA illimitata, PDF e molto altro",
			description:
				"IA illimitata, esportazione PDF, sincronizzazione del calendario e molto altro: un solo piano, ogni funzione Stippl sbloccata. Annulla quando vuoi.",
		},
		pt: {
			title: "Stippl Pro: IA ilimitada, PDF e muito mais",
			description:
				"Viagens e roteiros com IA ilimitados, exportação em PDF, sincronização de calendário e mais. Um plano, todos os recursos liberados, cancele quando quiser.",
		},
	},
	"/chatgpt-travel-planner": {
		nl: {
			title: "ChatGPT reisplanner: sla je reis op | Stippl",
			description:
				"Plan je reis in ChatGPT en sla hem op in Stippl: je route op de kaart, dagen met datum en echte plekken. Plan samen, boek verblijven, budgetteer en pak in.",
		},
		de: {
			title: "ChatGPT Reiseplaner: vom Chat zur echten Reise | Stippl",
			description:
				"Plane deine Reise in ChatGPT, speichere sie in Stippl: Route auf der Karte, Tage mit Datum, echte Orte. Gemeinsam planen, Unterkünfte buchen, Budget, Packliste.",
		},
		fr: {
			title: "Planificateur de voyage ChatGPT | Stippl",
			description:
				"Planifiez votre voyage dans ChatGPT, enregistrez-le dans Stippl : itinéraire sur la carte, jours datés, lieux réels. Voyagez à plusieurs, réservez, budgétez.",
		},
		es: {
			title: "Planificador de viajes ChatGPT | Stippl",
			description:
				"Planifica tu viaje en ChatGPT y guárdalo en Stippl: ruta en el mapa, días con fecha y lugares reales. Planifica en grupo, reserva y controla el gasto.",
		},
		it: {
			title: "Pianificatore di viaggi ChatGPT | Stippl",
			description:
				"Pianifica il viaggio in ChatGPT, salvalo in Stippl: percorso sulla mappa, giorni con date e luoghi reali. In gruppo, con alloggi, budget e bagagli.",
		},
		pt: {
			title: "Planejador de viagem ChatGPT | Stippl",
			description:
				"Planeje sua viagem no ChatGPT, salve no Stippl: rota no mapa, dias com datas e lugares reais. Planeje em grupo, reserve hospedagens, controle orçamento e mala.",
		},
	},
	"/claude-travel-planner": {
		nl: {
			title: "Claude reisplanner: sla je reis op | Stippl",
			description:
				"Plan je reis in Claude en sla hem op in Stippl: je route op de kaart, dagen met datum en echte plekken. Plan samen, boek verblijven, budgetteer en pak in.",
		},
		de: {
			title: "Claude Reiseplaner: vom Chat zur echten Reise | Stippl",
			description:
				"Plane deine Reise in Claude, speichere sie in Stippl: Route auf der Karte, Tage mit Datum, echte Orte. Gemeinsam planen, Unterkünfte buchen, Budget, Packliste.",
		},
		fr: {
			title: "Planificateur de voyage Claude | Stippl",
			description:
				"Planifiez votre voyage dans Claude, enregistrez-le dans Stippl : itinéraire sur la carte, jours datés, lieux réels. Voyagez à plusieurs, réservez, budgétez.",
		},
		es: {
			title: "Planificador de viajes Claude | Stippl",
			description:
				"Planifica tu viaje en Claude y guárdalo en Stippl: ruta en el mapa, días con fecha y lugares reales. Planifica en grupo, reserva y controla el gasto.",
		},
		it: {
			title: "Pianificatore di viaggi Claude | Stippl",
			description:
				"Pianifica il viaggio in Claude, salvalo in Stippl: percorso sulla mappa, giorni con date e luoghi reali. In gruppo, con alloggi, budget e bagagli.",
		},
		pt: {
			title: "Planejador de viagem Claude | Stippl",
			description:
				"Planeje sua viagem no Claude, salve no Stippl: rota no mapa, dias com datas e lugares reais. Planeje em grupo, reserve hospedagens, controle orçamento e mala.",
		},
	},
};

/**
 * Feature pages whose ENTIRE visible body — not just the `<title>`/meta above — is translated.
 *
 * All 14 since 2026-09-24. The five generic `FeaturePage.tsx` pages always were. The nine bespoke
 * pages used to render their FAQ and how-to steps from the English `*_CONTENT` exports; they now
 * read them through the namespace JSON (`<ns>.howToUse.*`, `<ns>.howToSteps.<n>.*`,
 * `<ns>.faqItems.<n>.*`), which the stippl-knowledge feature-page builder writes in all seven
 * languages. The one English block left is the itinerary planner's app-store reviews, which are
 * real quotes and stay as written.
 *
 * Remove a page from this set if its body stops being fully translated: it drops back to
 * `noindex` with its localised title kept.
 */
export const FEATURE_PAGES_WITH_LOCALISED_BODY: ReadonlySet<LocalisedFeaturePath> = new Set<LocalisedFeaturePath>(LOCALISED_FEATURE_PATHS);
